import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { aiApi } from '../../utils/aiApi';

export interface AIMessage {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  timestamp: string;
}

export interface AIConversation {
  id: string;
  title: string;
  assistantId: string;
  messages: AIMessage[];
  status: 'active' | 'ended';
  createdAt: string;
  updatedAt: string;
}

interface AIConversationState {
  conversations: AIConversation[];
  currentConversation: AIConversation | null;
  loading: boolean;
  error: string | null;
}

const initialState: AIConversationState = {
  conversations: [],
  currentConversation: null,
  loading: false,
  error: null,
};

export const fetchAIConversations = createAsyncThunk(
  'aiConversations/fetchAll',
  async () => {
    const response = await aiApi.get('/conversations');
    return response.data;
  }
);

export const fetchAIConversation = createAsyncThunk(
  'aiConversations/fetchOne',
  async (id: string) => {
    const response = await aiApi.get(`/conversations/${id}`);
    return response.data;
  }
);

export const sendAIMessage = createAsyncThunk(
  'aiConversations/sendMessage',
  async ({ conversationId, content }: { conversationId: string; content: string }) => {
    const response = await aiApi.post(`/conversations/${conversationId}/messages`, { content });
    return response.data;
  }
);

const aiConversationSlice = createSlice({
  name: 'aiConversations',
  initialState,
  reducers: {
    setCurrentConversation: (state, action) => {
      state.currentConversation = action.payload;
    },
    addMessage: (state, action) => {
      if (state.currentConversation) {
        state.currentConversation.messages.push(action.payload);
      }
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAIConversations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAIConversations.fulfilled, (state, action) => {
        state.loading = false;
        state.conversations = action.payload;
      })
      .addCase(fetchAIConversations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch conversations';
      })
      .addCase(fetchAIConversation.fulfilled, (state, action) => {
        state.currentConversation = action.payload;
      })
      .addCase(sendAIMessage.fulfilled, (state, action) => {
        if (state.currentConversation) {
          state.currentConversation.messages.push(action.payload);
        }
      });
  },
});

export const { setCurrentConversation, addMessage, clearError } = aiConversationSlice.actions;
export default aiConversationSlice.reducer;